import React, { useState, useEffect, forwardRef, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { EditorState, EditorBlock } from 'draft-js'
import {
  getSelectionAnchorPlacement,
  placeSelectionAnchor,
  embedAtomic
} from '../../../../utils/draftjs'
import EmbeddedVideo from '../../Loom/EmbeddedVideo'
import {
  addEditorRecoveryData,
  loomInsertionComplete
} from '../../../../actions/loom'
import { showModal } from '../../../../actions/_modal'
import {
  contentFromHTMLWithVideo,
  contentToHTMLWithVideo
} from '../videoSeralization'

/**
 * HOC that adds core Loom functionality to a given Draft js editor
 *  - Loom videos are embedded in the editor
 *    - Insertion happens automatically when provided with loomData that
 *      contains videoData with a src for a loom video
 *  - Automatically saves/loads dirty editor state while recording, so the user
 *    can navigate away and their unsaved changes will repopulate the editor
 *    when they return
 *  - Can inform parent component of whether or not a recording is active that
 *    is tied to this particular editor
 */

const renderAtom = (atomicBlock, size) => ({
  component: EmbeddedVideo,
  editable: false,
  props: {
    size: size
  }
})

const withLoomIntegration = BaseEditor =>
  forwardRef((props, ref) => {
    const {
      editorState,
      onChange,
      loomData,
      sessionId,
      setIsRecording,
      initialized = true,
      serialize = contentToHTMLWithVideo,
      deserialize = contentFromHTMLWithVideo,
      blockRendererFn,
      handleKeyCommand,
      videoSize
    } = props

    const [loadingRecovery, setLoadingRecovery] = useState(true)
    const [newEditor, setNewEditor] = useState(true)
    const embedding = useRef(false)
    const resetting = useRef(false)
    const willNavigateAway = useRef(false)

    const dispatch = useDispatch()

    useEffect(() => {
      if (setIsRecording) {
        setIsRecording(loomData && loomData.active)
      }
    }, [loomData])

    useEffect(() => {
      if (loadingRecovery) {
        resetting.current = false
      }
    }, [loadingRecovery])

    useEffect(() => {
      return () => {
        willNavigateAway.current = true
      }
    }, [sessionId])

    // Load recovery data during recording
    useEffect(() => {
      if (initialized && !resetting.current) {
        if (loomData && loomData.recovery) {
          // Parent will handle recovery content on first render to avoid waiting for initilization (which would cause a noticeable delay)
          const recoveredState = newEditor
            ? editorState
            : EditorState.createWithContent(
                deserialize(loomData.recovery.content)
              )
          const withSelection = placeSelectionAnchor(
            recoveredState,
            loomData.recovery.selection
          )
          onChange(withSelection)
        }
        setLoadingRecovery(false)
        setNewEditor(false)
      }
    }, [initialized, resetting.current, sessionId])

    // Embed a video when a link becomes available
    useEffect(() => {
      if (
        !embedding.current &&
        !resetting.current &&
        !loadingRecovery &&
        loomData &&
        loomData.videoData
      ) {
        embedding.current = true
        embedLoom(loomData.videoData)
        // Timeout protects against bugs caused by a quick remount (often from query invalidation)
        setTimeout(() => {
          dispatch(loomInsertionComplete())
          embedding.current = false
        }, 1000)
      }
    }, [loomData, loadingRecovery, resetting.current])

    // Store editor state when navigating away during a recording
    useEffect(() => {
      return () => {
        if (willNavigateAway.current) {
          if (
            !loadingRecovery &&
            !resetting.current &&
            loomData &&
            loomData.active &&
            loomData.sessionId === sessionId
          ) {
            dispatch(
              addEditorRecoveryData(
                sessionId,
                serialize(editorState.getCurrentContent()),
                getSelectionAnchorPlacement(editorState)
              )
            )
          }
          // In case editor doesn't get unmounted after navigation
          resetting.current = true
          willNavigateAway.current = false
          setLoadingRecovery(true)
        }
      }
    }, [sessionId, loomData, editorState, loadingRecovery])

    const thisBlockRendererFn = block => {
      if (block.getType() === 'atomic' && block.getData().get('src')) {
        return renderAtom(block, videoSize)
      }
      return blockRendererFn ? blockRendererFn(block) : null
    }

    const thisHandleKeyCommand = command => {
      if (command === 'split-block') {
        const currentContent = editorState.getCurrentContent()
        const selection = editorState.getSelection()
        const block = currentContent.getBlockForKey(selection.getAnchorKey())

        // Make it impossible to split atomic blocs
        // This shouldn't be possible anyway, it's a bug with draft
        // See https://github.com/facebook/draft-js/pull/2066 for details
        if (block.getType() === 'atomic') {
          return 'handled'
        }
      }
      return handleKeyCommand ? handleKeyCommand(command) : 'not-handled'
    }

    const embedLoom = ({ embedUrl, width, height }) => {
      if (embedUrl) {
        const newState = embedAtomic(
          editorState,
          { src: embedUrl, ratio: width / height },
          'VIDEO'
        )
        onChange(newState)

        const wrapper = document.getElementById(`editor-wrapper-${sessionId}`)
        if (wrapper) wrapper.scrollIntoView({ block: 'center' })
      } else {
        dispatch(showModal('GENERAL_ERROR'))
      }
    }

    return (
      <div id={`editor-wrapper-${sessionId}`}>
        <BaseEditor
          {...props}
          ref={ref}
          blockRendererFn={thisBlockRendererFn}
          handleKeyCommand={thisHandleKeyCommand}
        />
      </div>
    )
  })

export default withLoomIntegration
